/**
 * Section 1 — patient selection. Compact searchbox: the options list opens as
 * an absolute dropdown overlay (never pushes the layout), filters by name or
 * phone, closes on selection / outside click. The selected patient becomes
 * the input value with a clear (X) affordance.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { MagnifyingGlass, User, Check, X } from "@phosphor-icons/react";
import ContentLoader from "../../../../shared/ContentLoader";
import { sortPatientsByName } from "../../../../../utils/patientSortUtils";

function PatientSelectStep({ wiz, patients = [], isLoading }) {
  const { patientId, setPatientId } = wiz;
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const selected = patients.find((p) => p.id === patientId) || null;
  const selectedName = selected
    ? `${selected.firstName || ""} ${selected.lastName || ""}`.trim()
    : "";

  const filtered = useMemo(() => {
    const sorted = sortPatientsByName(patients);
    const q = query.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((p) => {
      const name = `${p.firstName || ""} ${p.lastName || ""}`.toLowerCase();
      const phone = (p.phone || p.mobilePhone || "").replace(/[^0-9]/g, "");
      const qDigits = q.replace(/[^0-9]/g, "");
      return (
        name.includes(q) || (qDigits.length >= 3 && phone.includes(qDigits))
      );
    });
  }, [patients, query]);

  useEffect(() => {
    const onDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, []);

  const clearSelection = () => {
    setPatientId(null);
    setQuery("");
    setOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div className="prw-step-panel">
      <div className="prw-patient-select" ref={containerRef}>
        <div className={`prw-search-box ${selected ? "has-value" : ""}`}>
          <MagnifyingGlass size={16} className="prw-search-icon" />
          <input
            ref={inputRef}
            type="text"
            value={open ? query : selectedName || query}
            placeholder="חיפוש מטופל לפי שם או טלפון..."
            onChange={(e) => {
              const val = e.target.value;
              setQuery(val);
              // The options list appears only while typing — never as a
              // stray open panel on focus.
              setOpen(val.trim().length > 0);
            }}
            onFocus={() => setQuery("")}
            role="combobox"
            aria-label="חיפוש מטופל"
            aria-expanded={open}
            aria-controls="prw-patient-listbox"
          />
          {selected && !open && (
            <button
              type="button"
              className="prw-search-clear"
              onClick={clearSelection}
              aria-label="ניקוי בחירת מטופל"
            >
              <X size={12} weight="bold" />
            </button>
          )}
        </div>

        {open && (
          <div className="prw-patient-list" role="listbox" id="prw-patient-listbox">
            {isLoading && (
              <ContentLoader row size="xs" message="טוען מטופלים..." />
            )}
            {!isLoading && filtered.length === 0 && (
              <div className="prw-patient-empty">לא נמצאו מטופלים</div>
            )}
            {!isLoading &&
              filtered.map((p) => {
                const isSel = p.id === patientId;
                return (
                  <button
                    key={p.id}
                    type="button"
                    role="option"
                    aria-selected={isSel}
                    className={`prw-patient-row ${isSel ? "selected" : ""}`}
                    onClick={() => {
                      setPatientId(p.id);
                      setQuery("");
                      setOpen(false);
                    }}
                  >
                    <span className="prw-patient-avatar">
                      <User size={14} weight="fill" />
                    </span>
                    <span className="prw-patient-name">
                      {p.firstName} {p.lastName}
                    </span>
                    {isSel && (
                      <Check
                        size={14}
                        weight="bold"
                        className="prw-patient-check"
                      />
                    )}
                  </button>
                );
              })}
          </div>
        )}
      </div>
    </div>
  );
}

export default PatientSelectStep;
